import Container from 'typedi'
import { Action, Module, Mutation, VuexModule } from 'vuex-module-decorators'
import LearningService from '@/modules/level-two/services/learning.service'
import type { LmsGroup } from '@/modules/level-two/models/learning.model'
import type {
  AddCEUPointsToCoursePayload,
  CEU,
  CompanyPayload,
  CreateOrEditLmsCeuPayload,
  DeleteLmsCeuPayload,
  EditCEUPointsOnCoursePayload,
  RemoveCEUPointFromCoursePayload
} from '@/modules/common/models'
import ErrorHandlerService from '@/modules/common/services/error-handler.service'

@Module({
  namespaced: true
})
export default class LearningStore extends VuexModule {
  lms_groups: LmsGroup[] = []
  assignedGroup: LmsGroup | null = null
  assignedCertificate: any = null
  lessonAssessmentChangelog: any = {}
  lessonAssessmentDevVersion: any = {}
  lessonAssessmentLiveVersion: any = {}
  ceus: CEU[] = []

  @Mutation
  SET_LMS_GROUPS (payload: any) {
    this.lms_groups = payload
  }

  @Mutation
  SET_ASSIGNED_GROUP (payload: LmsGroup) {
    this.assignedGroup = payload
  }

  @Mutation
  SET_ASSIGNED_LMS_CERTIFICATE (payload: any) {
    this.assignedCertificate = payload
  }

  @Mutation
  SET_LMS_CEUS (payload: CEU[]) {
    this.ceus = payload
  }

  @Mutation
  SET_LESSON_ASSESSMENT_CHANGELOG (payload: any) {
    this.lessonAssessmentChangelog = payload
  }

  @Mutation
  SET_LESSON_ASSESSMENT_LIVE_VERSION (payload: any) {
    this.lessonAssessmentLiveVersion = payload
  }

  @Mutation
  SET_LESSON_ASSESSMENT_DEV_VERSION (payload: any) {
    this.lessonAssessmentDevVersion = payload
  }

  @Action({ rawError: true })
  async fetchGroups (company_uuid: string) {
    const response = await Container.get(LearningService).fetchGroups(company_uuid)
    this.context.commit('SET_LMS_GROUPS', response.data)
    return response
  }

  @Action({ rawError: true })
  async createGroup (payload: any) {
    const { company_uuid, data } = payload
    return await Container.get(LearningService).createGroup(company_uuid, data)
  }

  @Action({ rawError: true })
  async updateGroup (payload: any) {
    const { company_uuid, lms_group_uuid, data } = payload
    return await Container.get(LearningService).updateGroup(company_uuid, lms_group_uuid, data)
  }

  @Action({ rawError: true })
  async getGroup (payload: any) {
    const { company_uuid, lms_group_uuid } = payload
    return await Container.get(LearningService).getGroup(company_uuid, lms_group_uuid)
  }

  @Action({ rawError: true })
  async syncLmsGroups (company_uuid: string) {
    return await Container.get(LearningService).syncLmsGroups(company_uuid)
  }

  @Action({ rawError: true })
  async assignCourseToGroup ({ company_uuid, course_uuid, lms_group_uuid }) {
    const response = await Container.get(LearningService).assignCourseToGroup(company_uuid, course_uuid, lms_group_uuid)
    await this.context.dispatch('getGroupForCourse', { company_uuid, course_uuid })
    return response
  }

  @Action({ rawError: true })
  async getGroupForCourse ({ company_uuid, course_uuid }) {
    const response = await Container.get(LearningService).getGroupForCourse(company_uuid, course_uuid)
    this.context.commit('SET_ASSIGNED_GROUP', response.data)
    return response
  }

  @Action({ rawError: true })
  async removeCourseFromGroup ({ company_uuid, course_uuid, lms_group_uuid }) {
    const response = await Container.get(LearningService).removeCourseFromGroup(company_uuid, course_uuid, lms_group_uuid)
    await this.context.dispatch('getGroupForCourse', { company_uuid, course_uuid })
    return response
  }

  @Action({ rawError: true })
  async getContentByType (payload: any) {
    const { event_uuid, type, options } = payload
    const response = await Container.get(LearningService).getContentByType(event_uuid, type, options)
    return response.data
  }

  @Action({ rawError: true })
  async getChapterContents (payload: any) {
    const { event_uuid, chapter_uuid, env, no_data } = payload
    if (event_uuid && chapter_uuid) {
      const response = await Container.get(LearningService).getChapterContents(event_uuid, chapter_uuid, env, no_data)
      return response.data
    }
  }

  @Action({ rawError: true })
  async getLessonAssessmentChangelog (payload: any) {
    const { event_uuid, type, sub_type } = payload
    if (event_uuid) {
      const response = await Container.get(LearningService).getContentByTypeAndSubtypeNoData(event_uuid, type, sub_type)
      this.context.commit('SET_LESSON_ASSESSMENT_CHANGELOG', response.data)
      return response.data
    }
  }

  @Action({ rawError: true })
  async getLessonAssessmentVersions (payload: any) {
    const { event_uuid, type, sub_type } = payload
    if (event_uuid) {
      const response = await Container.get(LearningService).getContentVersionsByTypeAndSubtype(event_uuid, type, sub_type)
      if (response?.data) {
        const data = response.data as { dev: any; live: any }
        this.context.commit('SET_LESSON_ASSESSMENT_DEV_VERSION', data.dev || {})
        this.context.commit('SET_LESSON_ASSESSMENT_LIVE_VERSION', data.live || {})
      }
      return response
    }
  }

  @Action({ rawError: true })
  async getLessonAssessmentContentByUUID (payload: any) {
    const { event_uuid, content_uuid } = payload
    if (event_uuid && content_uuid) {
      const response = await Container.get(LearningService).getContentByUUID(event_uuid, content_uuid)
      this.context.commit('SET_LESSON_ASSESSMENT_DEV_VERSION', response.data)
      return response
    }
  }

  @Action({ rawError: true })
  async getTableOfContents ({ event_uuid }) {
    return await Container.get(LearningService).getTableOfContents(event_uuid)
  }

  @Action({ rawError: true })
  async saveContent (payload: any) {
    const { event_uuid, type, sub_type, data } = payload
    if (event_uuid) {
      return await Container.get(LearningService).saveContent(event_uuid, type, sub_type, data)
    }
  }

  @Action({ rawError: true })
  async bulkUpdateContentOrder ({ event_uuid, data }) {
    if (event_uuid) {
      return await Container.get(LearningService).bulkUpdateContentOrder(event_uuid, data)
    }
  }

  @Action({ rawError: true })
  async deleteContent (payload: any) {
    const { event_uuid, type, sub_type } = payload
    if (event_uuid) {
      return await Container.get(LearningService).deleteContent(event_uuid, type, sub_type)
    }
  }

  @Action({ rawError: true })
  async deleteAllContentOfType (payload: any) {
    const { event_uuid, type } = payload
    if (event_uuid) {
      return await Container.get(LearningService).deleteAllContentOfType(event_uuid, type)
    }
  }

  @Action({ rawError: true })
  async fetchLmsCeus ({ company_uuid }: CompanyPayload): Promise<any> {
    const { data: ceus } = await Container.get(LearningService).fetchLmsCeus(company_uuid)
    this.context.commit('SET_LMS_CEUS', ceus)
    return ceus
  }

  @Action({ rawError: true })
  async createOrEditLmsCeu ({ company_uuid, ceu }: CreateOrEditLmsCeuPayload): Promise<any> {
    return await Container.get(LearningService).createOrEditLmsCeu(company_uuid, ceu)
  }

  @Action({ rawError: true })
  async deleteLmsCeu ({ company_uuid, ceu_uuid }: DeleteLmsCeuPayload): Promise<any> {
    return await Container.get(LearningService).deleteLmsCeu(company_uuid, ceu_uuid)
  }

  @Action({ rawError: true })
  async updateCertificate (payload: any) {
    const { company_uuid, certificate_uuid, data } = payload
    return await Container.get(LearningService).updateCertificate(company_uuid, certificate_uuid, data)
  }

  @Action({ rawError: true })
  async fetchCertificates ({ company_uuid }: CompanyPayload) {
    return await Container.get(LearningService).fetchCertificates(company_uuid)
  }

  @Action({ rawError: true })
  async createCertificate (payload: any) {
    const { company_uuid, data } = payload
    return await Container.get(LearningService).createCertificate(company_uuid, data)
  }

  @Action({ rawError: true })
  async fetchLmsStatistics ({ company_uuid }: CompanyPayload): Promise<any> {
    return await Container.get(LearningService).fetchLmsStatistics(company_uuid)
  }

  @Action({ rawError: true })
  async refetchLmsStatistics ({ company_uuid }: CompanyPayload): Promise<any> {
    return await Container.get(LearningService).refetchLmsStatistics(company_uuid)
  }

  @Action({ rawError: true })
  async fetchLmsStatisticsStatus ({ company_uuid }: CompanyPayload): Promise<any> {
    return await Container.get(LearningService).fetchLmsStatisticsStatus(company_uuid)
  }

  @Action({ rawError: true })
  async addCEUPointToCourse ({ company_uuid, course_uuid, ...payload }: AddCEUPointsToCoursePayload): Promise<any> {
    try {
      return await Container.get(LearningService).addCEUPointsToCourse(company_uuid, course_uuid, payload)
    } catch (e) {
      Container.get(ErrorHandlerService).error(e)
      return Promise.reject(e)
    }
  }

  @Action({ rawError: true })
  async editCEUPointOnCourse ({ company_uuid, course_uuid, point_uuid, ...payload }: EditCEUPointsOnCoursePayload): Promise<any> {
    try {
      return await Container.get(LearningService).editCEUPointsToCourse(company_uuid, course_uuid, point_uuid, payload)
    } catch (e) {
      Container.get(ErrorHandlerService).error(e)
      return Promise.reject(e)
    }
  }

  @Action({ rawError: true })
  async removeCEUPointFromCourse ({ company_uuid, course_uuid }: RemoveCEUPointFromCoursePayload): Promise<any> {
    try {
      return await Container.get(LearningService).removeCEUPointsToCourse(company_uuid, course_uuid)
    } catch (e) {
      Container.get(ErrorHandlerService).error(e)
      return Promise.reject(e)
    }
  }

  @Action({ rawError: true })
  async addCertificateToCourse ({ company_uuid, course_uuid, certificate_uuid }): Promise<any> {
    try {
      const response = await Container.get(LearningService).addCertificateToCourse(company_uuid, course_uuid, certificate_uuid)
      await this.context.dispatch('getCertificateForCourse', { company_uuid, course_uuid })
      return response
    } catch (e) {
      Container.get(ErrorHandlerService).error(e)
      return Promise.reject(e)
    }
  }

  @Action({ rawError: true })
  async getCertificateForCourse ({ company_uuid, course_uuid }): Promise<any> {
    try {
      const response = await Container.get(LearningService).getCertificateForCourse(company_uuid, course_uuid)
      this.context.commit('SET_ASSIGNED_LMS_CERTIFICATE', response.data)
      return response
    } catch (e) {
      Container.get(ErrorHandlerService).error(e)
      return Promise.reject(e)
    }
  }

  @Action({ rawError: true })
  async removeCertificateFromCourse ({ company_uuid, course_uuid, certificate_uuid }): Promise<any> {
    try {
      const response = await Container.get(LearningService).removeCertificateFromCourse(company_uuid, course_uuid, certificate_uuid)
      await this.context.dispatch('getCertificateForCourse', { company_uuid, course_uuid })
      return response
    } catch (e) {
      Container.get(ErrorHandlerService).error(e)
      return Promise.reject(e)
    }
  }

  @Action({ rawError: true })
  async seedLearners ({ company_uuid, course_uuid }): Promise<any> {
    try {
      return await Container.get(LearningService).seedLearners(company_uuid, course_uuid)
    } catch (e) {
      Container.get(ErrorHandlerService).error(e)
      return Promise.reject(e)
    }
  }
}
